import React, { useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { getWeatherForProject } from '../utils/api';
import styles from './WeatherCard.module.css';

// Compact 3-slot weather card for the Daily Report. Mirrors the layout in
// the Autodesk-style printed report: daily rain header, then 7am / 12pm /
// 4pm columns with condition icon + temperature + wind + humidity.
//
// Behavior:
//   - On mount + whenever projectId/date change, fetches /api/weather and
//     pushes the snapshot back to the parent via onSnapshot so it can be
//     persisted into submission.data.weather.
//   - If a `cached` snapshot is supplied (from a saved draft), shows that
//     immediately and skips the auto-fetch — the user can hit "Refresh"
//     to re-pull. This keeps a submitted report's weather frozen at submit
//     time even if you re-view it weeks later.
//   - 400 'no address' from the server renders a polite empty state with
//     a hint to the project settings page.

export default function WeatherCard({ projectId, date, cached, onSnapshot, readOnly }) {
  const [data, setData]       = useState(cached || null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  // Track which (projectId, date) pair the cached value came from, so we
  // know to refetch when the user changes the form date in the editor.
  const cacheKeyRef = useRef(cached ? `${projectId}|${date}` : null);

  async function fetchNow({ force = false } = {}) {
    if (!projectId || !date) return;
    setLoading(true); setError(null);
    try {
      const w = await getWeatherForProject(projectId, date);
      setData(w);
      cacheKeyRef.current = `${projectId}|${date}`;
      onSnapshot?.(w);
    } catch (err) {
      // Pull the server's friendly error message if present.
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // First-load + when (projectId, date) change. We respect the cache: if
  // the parent gave us a cached snapshot AND it matches the current key,
  // we don't refetch.
  useEffect(() => {
    if (!projectId || !date) return;
    const currentKey = `${projectId}|${date}`;
    if (data && cacheKeyRef.current === currentKey) return;
    fetchNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, date]);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>Weather</span>
        <div className={styles.headerRight}>
          {data?.daily && (
            <span className={styles.rain}>
              <RainIcon /> Rain: {data.daily.precipitation_in?.toFixed(2) ?? '—'} in
            </span>
          )}
          {!readOnly && (
            <button
              className={styles.refreshBtn}
              onClick={() => fetchNow({ force: true })}
              disabled={loading || !projectId || !date}
              title="Refresh weather"
            >
              <RefreshIcon spinning={loading} />
            </button>
          )}
        </div>
      </header>

      {loading && !data && (
        <div className={styles.empty}>Loading weather…</div>
      )}

      {error && (
        <div className={styles.errorBox}>
          {error}
        </div>
      )}

      {data && !loading && !error && (
        <>
          <div className={styles.slots}>
            {['07:00', '12:00', '16:00'].map((slot) => (
              <Slot key={slot} hour={slot} info={data.hours?.[slot]} />
            ))}
          </div>
          {data.fetched_at && (
            <div className={styles.footer}>
              Updated {format(parseISO(data.fetched_at), 'MMM d, yyyy h:mm a')}
              {data.source && <> · <span className={styles.source}>{data.source}</span></>}
            </div>
          )}
        </>
      )}

      {!data && !loading && !error && (
        <div className={styles.empty}>No weather yet.</div>
      )}
    </div>
  );
}

function Slot({ hour, info }) {
  const label = hour === '07:00' ? '7:00 AM' : hour === '12:00' ? '12:00 PM' : '4:00 PM';
  if (!info) {
    return (
      <div className={styles.slot}>
        <div className={styles.slotTime}>{label}</div>
        <div className={styles.slotMissing}>—</div>
      </div>
    );
  }
  return (
    <div className={styles.slot}>
      <div className={styles.slotTime}>{label}</div>
      <div className={styles.slotCond}>{info.condition}</div>
      <div className={styles.slotIcon}><WeatherIcon kind={info.icon} /></div>
      <div className={styles.slotMetrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Temp</span>
          <span className={styles.metricValue}>{Math.round(info.temperature_f)}°F</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Wind</span>
          <span className={styles.metricValue}>
            {Math.round(info.wind_mph)} mph
            <WindArrow degrees={info.wind_dir_deg} />
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Humidity</span>
          <span className={styles.metricValue}>{Math.round(info.humidity_pct)}%</span>
        </div>
      </div>
    </div>
  );
}

// ── Icons (small, inline; tinted via CSS) ──
function WeatherIcon({ kind }) {
  switch (kind) {
    case 'sun':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="9" fill="var(--yellow)" stroke="var(--yellow)" strokeWidth="1.4"/>
          {[0,45,90,135,180,225,270,315].map((deg) => {
            const r = 16, R = 22;
            const rad = deg * Math.PI / 180;
            const x1 = 24 + r * Math.cos(rad), y1 = 24 + r * Math.sin(rad);
            const x2 = 24 + R * Math.cos(rad), y2 = 24 + R * Math.sin(rad);
            return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--yellow)" strokeWidth="2" strokeLinecap="round"/>;
          })}
        </svg>
      );
    case 'sun-cloud':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <circle cx="16" cy="16" r="6" fill="var(--yellow)"/>
          {[0,45,90,135,180,225,270,315].map((deg) => {
            const rad = deg * Math.PI / 180;
            const x1 = 16 + 9 * Math.cos(rad), y1 = 16 + 9 * Math.sin(rad);
            const x2 = 16 + 13 * Math.cos(rad), y2 = 16 + 13 * Math.sin(rad);
            return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--yellow)" strokeWidth="1.6" strokeLinecap="round"/>;
          })}
          <path d="M14 32a6 6 0 0 1 6-6 7 7 0 0 1 13 1 5 5 0 0 1-1 10H20a6 6 0 0 1-6-5z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
        </svg>
      );
    case 'cloud':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <path d="M10 30a7 7 0 0 1 7-7 8 8 0 0 1 15 1 6 6 0 0 1-1 12H17a7 7 0 0 1-7-6z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
        </svg>
      );
    case 'rain':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <path d="M10 26a7 7 0 0 1 7-7 8 8 0 0 1 15 1 6 6 0 0 1-1 12H17a7 7 0 0 1-7-6z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
          {[16, 24, 32].map((x, i) => (
            <line key={i} x1={x} y1="36" x2={x - 2} y2="42" stroke="var(--blue)" strokeWidth="1.8" strokeLinecap="round"/>
          ))}
        </svg>
      );
    case 'snow':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <path d="M10 26a7 7 0 0 1 7-7 8 8 0 0 1 15 1 6 6 0 0 1-1 12H17a7 7 0 0 1-7-6z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
          {[14, 22, 30].map((x, i) => (
            <text key={i} x={x} y={42} fill="var(--text-dim)" fontSize="10" fontWeight="700">*</text>
          ))}
        </svg>
      );
    case 'sleet':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <path d="M10 26a7 7 0 0 1 7-7 8 8 0 0 1 15 1 6 6 0 0 1-1 12H17a7 7 0 0 1-7-6z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
          <line x1="16" y1="36" x2="14" y2="42" stroke="var(--blue)" strokeWidth="1.8" strokeLinecap="round"/>
          <text x="22" y="42" fill="var(--text-dim)" fontSize="9" fontWeight="700">*</text>
          <line x1="32" y1="36" x2="30" y2="42" stroke="var(--blue)" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      );
    case 'storm':
      return (
        <svg width="44" height="44" viewBox="0 0 48 48" fill="none">
          <path d="M10 26a7 7 0 0 1 7-7 8 8 0 0 1 15 1 6 6 0 0 1-1 12H17a7 7 0 0 1-7-6z"
            fill="var(--surface3)" stroke="var(--text-dim)" strokeWidth="1.4"/>
          <path d="M22 30l-3 8h4l-2 6 6-9h-4l3-5z" fill="var(--yellow)"/>
        </svg>
      );
    default:
      return null;
  }
}

const RainIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M6 1c-2 3-3 4-3 6a3 3 0 1 0 6 0c0-2-1-3-3-6z" fill="var(--blue)" stroke="var(--blue)" strokeWidth="0.5"/>
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className={spinning ? styles.spin : ''}>
    <path d="M2 8a6 6 0 0 1 10.5-4M14 8A6 6 0 0 1 3.5 12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M12.5 2v3h-3M3.5 14v-3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Wind direction is the direction the wind is COMING FROM (meteorological
// convention). Arrow points FROM that direction → so we rotate by (deg + 180).
const WindArrow = ({ degrees }) => {
  if (degrees == null) return null;
  return (
    <svg
      width="10" height="10" viewBox="0 0 12 12" fill="none"
      style={{ transform: `rotate(${(degrees + 180) % 360}deg)`, marginLeft: 4, verticalAlign: '-1px' }}
    >
      <path d="M6 1v9M3 4l3-3 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
};
